/**
 * Query Keys Factory
 *
 * Centralized location for all React Query keys.
 *
 * Benefits:
 * - Type-safe query keys
 * - Easy invalidation and prefetching
 * - Consistent naming conventions
 * - Hierarchical structure for easy partial invalidation
 *
 * Pattern:
 * - Use arrays for query keys
 * - Structure: [domain, operation, ...parameters]
 * - More specific keys should extend more general ones
 *
 * Examples:
 * - ['users'] - All user-related queries
 * - ['users', 'list'] - User list query
 * - ['users', 'detail', '123'] - Specific user detail
 * - ['users', 'detail', '123', 'posts'] - User's posts
 */

type RsvpListFilters = {
  paginated?: boolean
  limit?: number
  offset?: number
  q?: string
  status?:
    | 'all'
    | 'attending'
    | 'not_attending'
    | 'maybe'
    | 'pending'
    | 'rejected'
  sortBy?: 'createdAt' | 'name'
  sortDir?: 'asc' | 'desc'
  tags?: string
  tagMode?: 'any' | 'all'
  hasInternalNote?: boolean
}

export const queryKeys = {
  // ==================== Auth ====================
  auth: {
    all: ['auth'] as const,
    currentUser: () => [...queryKeys.auth.all, 'current-user'] as const,
    validate: () => [...queryKeys.auth.all, 'validate'] as const,
  },

  // ==================== Users ====================
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },

  // ==================== Invitations (Digital) ====================
  invitations: {
    all: ['invitations'] as const,
    lists: () => [...queryKeys.invitations.all, 'list'] as const,
    list: (filters?: { status?: string }) =>
      [...queryKeys.invitations.lists(), filters] as const,
    details: () => [...queryKeys.invitations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.invitations.details(), id] as const,
    qrCodeOptions: () =>
      [...queryKeys.invitations.all, 'qr-code-options'] as const,
  },

  // ==================== Stationery Invitations ====================
  stationeryInvitations: {
    all: ['stationery-invitations'] as const,
    lists: () => [...queryKeys.stationeryInvitations.all, 'list'] as const,
    list: (filters?: { status?: string }) =>
      [...queryKeys.stationeryInvitations.lists(), filters] as const,
    details: () => [...queryKeys.stationeryInvitations.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.stationeryInvitations.details(), id] as const,
  },

  // ==================== Notifications ====================
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (filters?: { read?: boolean; type?: string }) =>
      [...queryKeys.notifications.lists(), filters] as const,
  },

  // ==================== Cart ====================
  cart: {
    all: ['cart'] as const,
    current: () => [...queryKeys.cart.all, 'current'] as const,
    hostingInfo: () => [...queryKeys.cart.all, 'hosting-info'] as const,
  },

  // ==================== Checkout ====================
  checkout: {
    all: ['checkout'] as const,
    totals: (promoCode?: string) =>
      [...queryKeys.checkout.all, 'totals', { promoCode }] as const,
    hostingTotals: (invitationId: string, promoCode?: string) =>
      [
        ...queryKeys.checkout.all,
        'hosting-totals',
        invitationId,
        { promoCode },
      ] as const,
    paymentStatus: (paymentIntentId: string) =>
      [...queryKeys.checkout.all, 'payment-status', paymentIntentId] as const,
  },

  // ==================== Orders ====================
  orders: {
    all: ['orders'] as const,
    list: (page?: number, limit?: number) =>
      [...queryKeys.orders.all, 'list', { page, limit }] as const,
    infinite: (limit?: number) =>
      [...queryKeys.orders.all, 'infinite', { limit }] as const,
    details: () => [...queryKeys.orders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.orders.details(), id] as const,
  },

  // ==================== Billing ====================
  billing: {
    all: ['billing'] as const,
    subscription: () => [...queryKeys.billing.all, 'subscription'] as const,
    transactions: () => [...queryKeys.billing.all, 'transactions'] as const,
    transactionList: (filters?: { page?: number; limit?: number }) =>
      [...queryKeys.billing.transactions(), filters] as const,
  },

  // ==================== Payments ====================
  payments: {
    all: ['payments'] as const,
    transactions: () => [...queryKeys.payments.all, 'transactions'] as const,
    transactionList: (limit: number, offset: number) =>
      [...queryKeys.payments.transactions(), { limit, offset }] as const,
    transactionDetails: () =>
      [...queryKeys.payments.all, 'transaction-detail'] as const,
    transactionDetail: (id: string) =>
      [...queryKeys.payments.transactionDetails(), id] as const,
  },

  // ==================== Settings ====================
  settings: {
    all: ['settings'] as const,
    userSettings: () => [...queryKeys.settings.all, 'user'] as const,
  },

  // ==================== Themes ====================
  themes: {
    all: ['themes'] as const,
    lists: () => [...queryKeys.themes.all, 'list'] as const,
    list: (filters?: { category?: string }) =>
      [...queryKeys.themes.lists(), filters] as const,
    details: () => [...queryKeys.themes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.themes.details(), id] as const,
  },

  // ==================== Gallery (Guest Uploads) ====================
  gallery: {
    all: ['gallery'] as const,
    details: () => [...queryKeys.gallery.all, 'detail'] as const,
    detail: (invitationId: string) =>
      [...queryKeys.gallery.details(), invitationId] as const,
  },

  // ==================== Keepsakes ====================
  keepsakes: {
    all: ['keepsakes'] as const,
    categories: () => [...queryKeys.keepsakes.all, 'categories'] as const,
    categoryProducts: (
      slug: string,
      params?: { page?: number; limit?: number; tags?: string[] }
    ) => [...queryKeys.keepsakes.all, 'category', slug, params] as const,
    products: () => [...queryKeys.keepsakes.all, 'product'] as const,
    product: (id: string) => [...queryKeys.keepsakes.products(), id] as const,
  },

  // ==================== RSVPs ====================
  rsvps: {
    all: ['rsvps'] as const,
    lists: () => [...queryKeys.rsvps.all, 'list'] as const,
    list: (invitationId: string, filters?: RsvpListFilters) =>
      filters
        ? ([...queryKeys.rsvps.lists(), invitationId, filters] as const)
        : ([...queryKeys.rsvps.lists(), invitationId] as const),
    infinite: (invitationId: string, filters?: RsvpListFilters) =>
      [...queryKeys.rsvps.list(invitationId, filters), 'infinite'] as const,
    tags: (invitationId: string) =>
      [...queryKeys.rsvps.all, 'tags', invitationId] as const,
  },

  // ==================== Guestbook ====================
  guestbook: {
    all: ['guestbook'] as const,
    lists: () => [...queryKeys.guestbook.all, 'list'] as const,
    list: (invitationId: string) =>
      [...queryKeys.guestbook.lists(), invitationId] as const,
  },

  // ==================== Broadcasts ====================
  broadcasts: {
    all: ['broadcasts'] as const,
    lists: () => [...queryKeys.broadcasts.all, 'list'] as const,
    list: (invitationId: string) =>
      [...queryKeys.broadcasts.lists(), invitationId] as const,
  },

  // ==================== Popup Campaigns ====================
  popupCampaigns: {
    all: ['popupCampaigns'] as const,
    active: (audience: string) =>
      [...queryKeys.popupCampaigns.all, 'active', audience] as const,
  },

  // ==================== Surveys ====================
  surveys: {
    all: ['surveys'] as const,
    active: (trigger?: string) =>
      [...queryKeys.surveys.all, 'active', { trigger }] as const,
    myResponses: () => [...queryKeys.surveys.all, 'my-responses'] as const,
    homepageReviews: (limit?: number) =>
      [...queryKeys.surveys.all, 'homepage-reviews', { limit }] as const,
  },
} as const
